import AccountBootstrapGuard from '@/components/guards/AccountBootstrapGuard'
import SubscriptionGuard from '@/components/guards/subscription-guard'
import SurveyGuard from '@/components/guards/survey-guard'
import DashboardLayout from '@/routes/dashboard/DashboardLayout'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { createCoreRoutes } from './core/core'
import { createContentViewerRoutes } from './contentViewer'
import { createMatterRoutes } from './matter/matter'
import type { RouteFactory } from '../types'
import { createLegacyRedirects } from './matter/legacyRedirects'

/**
 * Creates the dashboard route with all child routes assembled.
 * Wraps everything in SubscriptionGuard, AccountBootstrapGuard, and SurveyGuard.
 *
 * Route hierarchy:
 * - Core routes (home, admin, account, drives, settings, matters)
 * - Content viewer routes (reference-viewer)
 * - Matter-first routes (/dashboard/m/:matterId/...)
 * - Legacy redirects (catch old URLs and redirect to matter-first)
 */
export const createDashboardRoutes: RouteFactory = () => [
  {
    path: 'dashboard',
    element: (
      <SubscriptionGuard>
        <AccountBootstrapGuard>
          <SurveyGuard>
            <DashboardLayout />
          </SurveyGuard>
        </AccountBootstrapGuard>
      </SubscriptionGuard>
    ),
    children: [
      // Feature flag placeholder - kept for example/future use
      ...(kGetFeatureFlagStatus(kFeatureFlags.WEBSOCKET_CHAT) ? [] : []),

      // Core routes (home, admin, account, drives, settings, matters)
      ...createCoreRoutes(),

      // Content viewer routes
      ...createContentViewerRoutes(),

      // Matter-first routes
      // /dashboard/m/:matterId/chronology, /chat, /billing-summaries, /files
      ...createMatterRoutes(),

      // Legacy redirects: feature-first → matter-first URL migration
      // Catches old URLs like /dashboard/medical-chronology/m/:matterId and redirects to new matter-first URLs like /dashboard/m/:matterId/medical-chronology
      ...createLegacyRedirects(),
    ],
  },
]
